import * as THREE from 'three';
import Action from '../classes/Action';
import Engine from '../classes/Engine';
import { CustomEventTypes, SaveModificationTypes} from '../globals';
import DefaultSceneManager from '../classes/DefaultSceneManager';
import SceneObjectUtils from '../classes/utilities/SceneObjectUtils';
import ARTracker from '../classes/ARTracker';
import { assert } from '../classes/utilities/ErrorHandling';
import ProjectSaver from '../classes/ProjectSaver';
import { IObjectSelection } from '../interfaces/Misc';

export default class PasteLinkedObjectsAction extends Action
{
	constructor(defaultObjects: THREE.Object3D[], objects: THREE.Object3D[])
	{
		const previousSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
		const existingLinkedObjectsMap = new Map<THREE.Object3D, THREE.Object3D>();
		super([defaultObjects, objects, existingLinkedObjectsMap], [objects, previousSelection, existingLinkedObjectsMap], "Pasted Default-Linked Objects", [defaultObjects, objects, existingLinkedObjectsMap]);
	}

	do(defaultObjects: THREE.Object3D[], objects: THREE.Object3D[], existingLinkedObjectsMap: Map<THREE.Object3D, THREE.Object3D>) : void
	{
		Engine.getInstance().getPickingManager().clearSelection(false);

		for (const existingLinkedObject of existingLinkedObjectsMap.values())
		{
			DefaultSceneManager.getInstance().breakLink(existingLinkedObject);
		}

		for(let i = 0; i < defaultObjects.length; i++)
		{	
			const defaultObject = defaultObjects[i];
			const pastedObject = objects[i];

			SceneObjectUtils.parallelTraverse(pastedObject, defaultObject, function(linkedObjectChild, defaultObjectChild)
			{
				if(!DefaultSceneManager.getInstance().getDefaultObject(linkedObjectChild.userData.guid))
				{
					DefaultSceneManager.getInstance().linkObject(linkedObjectChild, defaultObjectChild, Engine.getInstance().getCurrentScene()!);
				}
			});

			const linkedParent = DefaultSceneManager.getInstance().getLinkedObject(defaultObject.parent!, Engine.getInstance().getCurrentScene()!.userData.guid);
			assert(linkedParent);
			Engine.getInstance().addObject(pastedObject, linkedParent!);
			ProjectSaver.getInstance().markModifiedObject(pastedObject, SaveModificationTypes.ReAdded);

			Engine.getInstance().getPickingManager().selectObject(pastedObject);

		}

		Engine.getInstance().getPickingManager().storeSelection();
		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
	}

	undo(objects: THREE.Object3D[], previousSelection: IObjectSelection, existingLinkedObjectsMap: Map<THREE.Object3D, THREE.Object3D>) : void
	{
		Engine.getInstance().getPickingManager().restoreSelection(previousSelection);

		for (const pastedObject of objects)
		{
			ProjectSaver.getInstance().markModifiedObject(pastedObject, SaveModificationTypes.Removed);
			DefaultSceneManager.getInstance().breakLink(pastedObject);
			Engine.getInstance().removeObject(pastedObject);
		}

		for (const [defaultObject, linkedObject] of existingLinkedObjectsMap.entries())
		{
			DefaultSceneManager.getInstance().linkObject(linkedObject, defaultObject, Engine.getInstance().getCurrentScene()!);
		}
	}

	verify(defaultObjects: THREE.Object3D[], objects: THREE.Object3D[], existingLinkedObjectsMap: Map<THREE.Object3D, THREE.Object3D>) : boolean
	{
		if(defaultObjects.length !== objects.length) return false;

		for(let i = defaultObjects.length-1; i >= 0; i--)
		{	
			const defaultObject = defaultObjects[i];

			if (defaultObject instanceof ARTracker)
			{
				defaultObjects.splice(i, 1);
				objects.splice(i, 1);
			}
			else
			{
				const linkedObject = DefaultSceneManager.getInstance().getLinkedObject(defaultObject, Engine.getInstance().getCurrentScene()!.userData.guid);
				const linkedParent = DefaultSceneManager.getInstance().getLinkedObject(defaultObject.parent!, Engine.getInstance().getCurrentScene()!.userData.guid);
	
				if((linkedObject && !linkedObject.userData.removedFromScene) || (!linkedParent || linkedParent.userData.removedFromScene))
				{
					defaultObjects.splice(i, 1);
					objects.splice(i, 1);
					continue;
				}
				else if(linkedObject)
				{
					linkedObject.traverse(function(child)
					{
						const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(child.userData.guid);
						if(defaultObject)
						{
							existingLinkedObjectsMap.set(defaultObject, child);
						}
					});
				}

				if(!objects[i].userData.markedSaveRoot)
				{
					const rootParent = linkedParent.userData.markedSaveRoot ? linkedParent : ProjectSaver.getInstance().getParentSaveRoot(linkedParent);
					objects[i].userData.ownerSaveRoot = rootParent.userData.guid;
				}

			}
			
		}

		return defaultObjects.length > 0;
	}
}
